//External
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
//Internal
import PriceRange from "../slider-range/price-range";
import useSheetScrollLock from "../common/useSheetScrollLock";
import IconChevronDown from "../icons/icons-chevron-down";
import { getSwatchColor } from "./colorSwatches";
import { SEAT_THRESHOLDS, seatsForThreshold, thresholdMatches } from "./seatTiers";
import useUnavailableOptions from "./useUnavailableOptions";
import { FilterEmptyOptions } from "./FilterDropdownPanel";
import { countMatchingVehicles } from "./availabilityPool";

/*******************
@purpose : white checkmark used inside a checked box
@Author : INIC
******************/
const CheckIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 5L4.2 8.2L11 1.4" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/*******************
@purpose : comma-joined titles of the selected options, or a placeholder
           when none are picked yet
@Parameter : {optionList, selectedValues, placeholder}
@Author : INIC
******************/
const summarizeSelection = (optionList, selectedValues, placeholder) => {
  const titles = (optionList || [])
    .filter((option) => selectedValues.includes(option.slug))
    .map((option) => option.title);
  return titles.length ? titles.join(", ") : placeholder;
};

/*******************
@Purpose : One row inside the Filters dialog that summarizes a multi-select
           (Categories/Brands) or single-select "X or more" (Seats) filter
           and expands into its checkbox/radio list when clicked. The panel
           is portaled to <body> and positioned via getBoundingClientRect so
           it floats on top of the whole dialog instead of being clipped by
           the scrollable .filters-dialog-body it lives in.
@Parameter : {label, isOpen, onToggle, onClose, children}
@Author : INIC
******************/
const DialogFilterRow = ({ label, isOpen, onToggle, onClose, children }) => {
  const containerRef = useRef();
  const triggerRef = useRef();
  const panelRef = useRef();
  const [panelStyle, setPanelStyle] = useState(null);

  /*******************
  @purpose : anchor the portaled panel directly under the trigger, in
             viewport coordinates (position: fixed) so it isn't clipped by
             the dialog body's overflow-y: auto. Also clamps the panel's
             height to whatever space is actually left between the trigger
             and the dialog's footer (falling back to the viewport edge if
             the footer can't be found) - on the mobile bottom-sheet layout,
             a row like Seats sits well down the sheet, so opening below it
             with the old fixed 280px max-height could push the panel's
             bottom past the visible screen/behind the footer, hiding the
             lower options entirely instead of just needing a scroll.
  @Author : INIC
  ******************/
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const margin = 8;
      const footer = containerRef.current
        ?.closest(".filters-dialog")
        ?.querySelector(".filters-dialog-footer");
      const lowerBound = footer ? footer.getBoundingClientRect().top : window.innerHeight;

      setPanelStyle({
        position: "fixed",
        top: rect.bottom + margin,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(lowerBound - rect.bottom - margin, 120),
      });
    };

    updatePosition();

    const scrollParent = containerRef.current?.closest(".filters-dialog-body");
    window.addEventListener("resize", updatePosition);
    scrollParent?.addEventListener("scroll", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
      scrollParent?.removeEventListener("scroll", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e) => {
      const clickedTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const clickedPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!clickedTrigger && !clickedPanel) {
        onClose();
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    return () => document.removeEventListener("pointerdown", handleOutsideClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div className="filters-dialog-section" ref={containerRef}>
      <div className="filters-dialog-section-label">{label}</div>
      <button
        type="button"
        className="filters-dialog-row-trigger"
        ref={triggerRef}
        onClick={onToggle}
      >
        <span className="filters-dialog-row-trigger-value">{children.summary}</span>
        <IconChevronDown className={`reservation-chevron ${isOpen ? "is-open" : ""}`} />
      </button>
      {isOpen &&
        panelStyle &&
        createPortal(
          <div className="filters-dialog-row-panel" ref={panelRef} style={panelStyle}>
            {children.options}
          </div>,
          document.body
        )}
    </div>
  );
};

/*******************
@Purpose : "Filters" dialog - a single modal covering every filter at once
           (Categories, Brands, Price Range, Seats, Colors), with one Reset
           and one live "Show N cars" that applies everything together.
           Separate from the individual pill dropdowns in the filter row -
           those stay for quick single-filter access; this is the advanced,
           all-in-one view opened from the funnel icon.
@Parameter : {isOpen, onClose, categoryList, brandList, colorList, priceBounds,
              selectedCategories, setSelectedCategories, selectedBrands,
              setSelectedBrands, priceRange, setPriceRange, selectedSeats,
              setSelectedSeats, selectedColors, setSelectedColors, pool}
@Author : INIC
******************/
function FiltersDialog({
  isOpen,
  onClose,
  categoryList,
  brandList,
  colorList,
  priceBounds,
  selectedCategories,
  setSelectedCategories,
  selectedBrands,
  setSelectedBrands,
  priceRange,
  setPriceRange,
  selectedSeats,
  setSelectedSeats,
  selectedColors,
  setSelectedColors,
  pool,
  baseFilters = {},
  hiddenFilters = [],
}) {
  const isHidden = (name) => hiddenFilters.includes(name);
  const [expandedSection, setExpandedSection] = useState(null);
  const [draftCategories, setDraftCategories] = useState(selectedCategories);
  const [draftBrands, setDraftBrands] = useState(selectedBrands);
  const [draftPriceRange, setDraftPriceRange] = useState(priceRange);
  const [draftSeats, setDraftSeats] = useState(selectedSeats);
  const [draftColors, setDraftColors] = useState(selectedColors);

  /*******************
  @purpose : start each open with a fresh draft copied from the last applied
             selections, so re-opening doesn't carry over an abandoned edit
  @Author : INIC
  ******************/
  useEffect(() => {
    if (!isOpen) return;

    setExpandedSection(null);
    setDraftCategories(selectedCategories);
    setDraftBrands(selectedBrands);
    setDraftPriceRange(priceRange);
    setDraftSeats(selectedSeats);
    setDraftColors(selectedColors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  /*******************
  @purpose : lock page scroll behind the dialog while it's open
  @Author : INIC
  ******************/
  useEffect(() => {
    document.body.classList.toggle("model-active", isOpen);
    return () => document.body.classList.remove("model-active");
  }, [isOpen]);

  useSheetScrollLock(isOpen);

  const draftMinPrice = draftPriceRange?.min ?? priceBounds?.min ?? 0;
  const draftMaxPrice = draftPriceRange?.max ?? priceBounds?.max ?? 0;

  const draftPriceFilters = {
    minPrice: draftPriceRange?.min ?? undefined,
    maxPrice: draftPriceRange?.max ?? undefined,
  };

  // Options that would show 0 vehicles if picked on their own (given every
  // other section's current draft) - hidden below rather than left
  // clickable as a dead end. Computed instantly off the shared availability
  // pool, so expanding a section never has to wait on a network round trip.
  const unavailableCategories = useUnavailableOptions({
    pool,
    filterKey: "categorySlugs",
    options: (categoryList || []).map((category) => ({ key: category.slug, values: [category.slug] })),
    otherFilters: {
      brandSlugs: draftBrands,
      ...draftPriceFilters,
      seats: draftSeats,
      colorIds: draftColors,
      ...baseFilters,
    },
    colorList,
  });

  const unavailableBrands = useUnavailableOptions({
    pool,
    filterKey: "brandSlugs",
    options: (brandList || []).map((brand) => ({ key: brand.slug, values: [brand.slug] })),
    otherFilters: {
      categorySlugs: draftCategories,
      ...draftPriceFilters,
      seats: draftSeats,
      colorIds: draftColors,
      ...baseFilters,
    },
    colorList,
  });

  const unavailableThresholds = useUnavailableOptions({
    pool,
    filterKey: "seats",
    options: SEAT_THRESHOLDS.map((threshold) => ({ key: threshold, values: seatsForThreshold(threshold) })),
    otherFilters: {
      categorySlugs: draftCategories,
      brandSlugs: draftBrands,
      ...draftPriceFilters,
      colorIds: draftColors,
      ...baseFilters,
    },
    colorList,
  });

  const unavailableColors = useUnavailableOptions({
    pool,
    filterKey: "colorIds",
    options: (colorList || []).map((color) => ({ key: color.id, values: [color.id] })),
    otherFilters: {
      categorySlugs: draftCategories,
      brandSlugs: draftBrands,
      ...draftPriceFilters,
      seats: draftSeats,
      ...baseFilters,
    },
    colorList,
  });

  const visibleCategoryList = (categoryList || []).filter(
    (category) => !unavailableCategories.has(category.slug) || draftCategories.includes(category.slug)
  );
  const visibleBrandList = (brandList || []).filter(
    (brand) => !unavailableBrands.has(brand.slug) || draftBrands.includes(brand.slug)
  );
  const visibleThresholds = SEAT_THRESHOLDS.filter(
    (threshold) => !unavailableThresholds.has(threshold) || thresholdMatches(threshold, draftSeats)
  );
  const visibleColorList = (colorList || []).filter(
    (color) => !unavailableColors.has(color.id) || draftColors.includes(color.id)
  );

  // "Show N cars" for everything currently drafted across every section -
  // recomputed instantly on every change, straight off the pool.
  const previewCount = countMatchingVehicles(
    pool,
    {
      categorySlugs: draftCategories,
      brandSlugs: draftBrands,
      ...draftPriceFilters,
      seats: draftSeats,
      colorIds: draftColors,
      ...baseFilters,
    },
    colorList
  );

  if (!isOpen) return null;

  const toggleSection = (section) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const toggleDraftValue = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const handleReset = () => {
    setDraftCategories([]);
    setDraftBrands([]);
    setDraftPriceRange({ min: null, max: null });
    setDraftSeats([]);
    setDraftColors([]);
  };

  const handleApply = () => {
    setSelectedCategories(draftCategories);
    setSelectedBrands(draftBrands);
    setPriceRange(draftPriceRange);
    setSelectedSeats(draftSeats);
    setSelectedColors(draftColors);
    onClose();
  };

  const appliedSeatThreshold = SEAT_THRESHOLDS.find((threshold) =>
    thresholdMatches(threshold, draftSeats)
  );

  // Portalled out of .results-filters for the same reason the pill panels are
  // (see FilterDropdownPanel) - that row is a horizontal touch scroller, and
  // iOS Safari clips position:fixed descendants of one against the scroller.
  return createPortal(
    <div
      className="filters-dialog-overlay"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="filters-dialog">
        <div className="filters-dialog-header">
          <h5>Filters</h5>
          <button type="button" className="filters-dialog-close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6L18 18" stroke="#202124" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M18 6L6.00003 18" stroke="#202124" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="filters-dialog-body">
          {!isHidden("categories") && (
            <DialogFilterRow
              label="Categories"
              isOpen={expandedSection === "categories"}
              onToggle={() => toggleSection("categories")}
              onClose={() => setExpandedSection(null)}
            >
              {{
                summary: summarizeSelection(categoryList, draftCategories, "Any category"),
                options: visibleCategoryList.length === 0 ? <FilterEmptyOptions /> : visibleCategoryList.map((category) => {
                  const checked = draftCategories.includes(category.slug);
                  return (
                    <label key={category.id} className="filter-checkbox-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          toggleDraftValue(draftCategories, setDraftCategories, category.slug)
                        }
                      />
                      <span className={`filter-checkbox-box ${checked ? "is-checked" : ""}`}>
                        {checked && <CheckIcon />}
                      </span>
                      <span className="filter-checkbox-label">{category.title}</span>
                    </label>
                  );
                }),
              }}
            </DialogFilterRow>
          )}

          {!isHidden("brands") && (
            <DialogFilterRow
              label="Brands"
              isOpen={expandedSection === "brands"}
              onToggle={() => toggleSection("brands")}
              onClose={() => setExpandedSection(null)}
            >
              {{
                summary: summarizeSelection(brandList, draftBrands, "Any brand"),
                options: visibleBrandList.length === 0 ? <FilterEmptyOptions /> : visibleBrandList.map((brand) => {
                  const checked = draftBrands.includes(brand.slug);
                  return (
                    <label key={brand.id} className="filter-checkbox-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDraftValue(draftBrands, setDraftBrands, brand.slug)}
                      />
                      <span className={`filter-checkbox-box ${checked ? "is-checked" : ""}`}>
                        {checked && <CheckIcon />}
                      </span>
                      <span className="filter-checkbox-label">{brand.title}</span>
                    </label>
                  );
                }),
              }}
            </DialogFilterRow>
          )}

          <div className="filters-dialog-section filters-dialog-price-range">
            <div className="filters-dialog-section-label">Price Range (Per Day)</div>
            <PriceRange
              filterPrice={priceBounds}
              minPrice={draftMinPrice}
              maxPrice={draftMaxPrice}
              handlePriceRange={([lo, hi]) => setDraftPriceRange({ min: lo, max: hi })}
              handleMinPriceChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (!isNaN(value)) setDraftPriceRange((prev) => ({ ...prev, min: value }));
              }}
              handleMaxPriceChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (!isNaN(value)) setDraftPriceRange((prev) => ({ ...prev, max: value }));
              }}
            />
          </div>

          <DialogFilterRow
            label="Seats"
            isOpen={expandedSection === "seats"}
            onToggle={() => toggleSection("seats")}
            onClose={() => setExpandedSection(null)}
          >
            {{
              summary: appliedSeatThreshold ? `${appliedSeatThreshold} or more` : "Any seat count",
              options: visibleThresholds.length === 0 ? <FilterEmptyOptions /> : visibleThresholds.map((threshold) => {
                const checked = thresholdMatches(threshold, draftSeats);
                return (
                  <label key={threshold} className="filter-checkbox-row">
                    <input
                      type="radio"
                      name="filters-dialog-seats"
                      checked={checked}
                      onChange={() => setDraftSeats(seatsForThreshold(threshold))}
                    />
                    <span className={`filter-radio-circle ${checked ? "is-checked" : ""}`}>
                      {checked && <span className="filter-radio-dot" />}
                    </span>
                    <span className="filter-checkbox-label">{threshold} or more</span>
                  </label>
                );
              }),
            }}
          </DialogFilterRow>

          <div className="filters-dialog-section">
            <div className="filters-dialog-section-label">Colors</div>
            <div className="filters-color-swatches">
              {visibleColorList.length === 0 && <FilterEmptyOptions />}
              {visibleColorList.map((color) => {
                const checked = draftColors.includes(color.id);
                return (
                  <button
                    type="button"
                    key={color.id}
                    className={`color-swatch ${checked ? "is-selected" : ""}`}
                    aria-label={color.name}
                    title={color.name}
                    onClick={() => toggleDraftValue(draftColors, setDraftColors, color.id)}
                  >
                    <span
                      className="color-swatch-fill"
                      style={{ backgroundColor: color.code || getSwatchColor(color.name) }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="filters-dialog-footer">
          <button type="button" className="filter-reset-btn" onClick={handleReset}>
            Reset
          </button>
          <button type="button" className="filter-apply-btn" onClick={handleApply}>
            {pool?.length
              ? `Show ${previewCount} ${previewCount === 1 ? "car" : "cars"}`
              : "Show cars"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default FiltersDialog;
